'use client';

import React, { createContext, useContext, useMemo, useState } from 'react';

type MessageStatus = 'success' | 'error' | 'info' | 'warning' | null;

export type AuthErrorContextValue = Readonly<{
  message: string | null;
  status: MessageStatus;
  setMessage: (msg: string, status: Exclude<MessageStatus, null>) => void;
  clearMessage: () => void;
}>;

const AuthenticationErrorContext = createContext<AuthErrorContextValue | null>(null);

export function AuthenticationErrorProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [message, setMessageState] = useState<string | null>(null);
  const [status, setStatus] = useState<MessageStatus>(null);

  const setMessage = (msg: string, nextStatus: Exclude<MessageStatus, null>) => {
    setMessageState(msg);
    setStatus(nextStatus);
  };

  const clearMessage = () => {
    setMessageState(null);
    setStatus(null);
  };

  const value: AuthErrorContextValue = useMemo(
    () => ({
      message,
      status,
      setMessage,
      clearMessage,
    }),
    [message, status]
  );

  return <AuthenticationErrorContext.Provider value={value}>{children}</AuthenticationErrorContext.Provider>;
}

export function useAuthenticationError(): AuthErrorContextValue {
  const ctx = useContext(AuthenticationErrorContext);
  if (!ctx) {
    throw new Error('useAuthenticationError must be used within an AuthenticationErrorProvider');
  }
  return ctx;
}
